import { gql } from "@apollo/client";
import {
  CategoryId,
  HexId,
  OmitRecursively,
  ProjectLanguage,
  StatusId,
  TaoProjectId,
  asciiCompare,
} from "@hex/common";
import {
  Action,
  EntityState,
  PayloadAction,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { useCallback } from "react";

import { useDispatch, useSelector } from "../hooks.js";
import { RootState } from "../store.js";
import { createSelector } from "../utils/index.js";

import { HexSelectionFragment } from "./hexSelectionSlice.generated.js";

gql`
  fragment HexSelectionFragment on Hex {
    id
    canDelete
    canEdit
    canEditMetadata
    canSetOwner
    canUseApp
    projectLanguage
    status {
      id
    }
    categories {
      id
    }
  }
`;

type HexSelectionPerm =
  | "canDelete"
  | "canEdit"
  | "canEditMetadata"
  | "canSetOwner"
  | "canUseApp";

type HexSelectionData = OmitRecursively<HexSelectionFragment, "__typename">;
type TaoSelectionData = {
  taoProjectId: TaoProjectId;
  canDelete: boolean;
  canEditMetadata: boolean;
  status: { id: StatusId } | null;
  categories: ReadonlyArray<{ id: CategoryId }>;
};
export type SelectedProject =
  | { selectionKey: string; type: "hex"; hex: HexSelectionData }
  | { selectionKey: string; type: "tao"; tao: TaoSelectionData };

const selectedProjectAdapter = createEntityAdapter<SelectedProject, string>({
  selectId: (project) => project.selectionKey,
  sortComparer: (a, b) => asciiCompare(a.selectionKey, b.selectionKey),
});

export type HexSelectionSliceState = {
  selectedProjects: EntityState<SelectedProject, string>;
  // AG grid currently does not support controlled selection state
  // @see https://ag-grid.zendesk.com/hc/en-us/requests/32181
  // so we do this 'hack' to avoid ag grid from accidentally causing a circular update
  // and inverting the selection state on range selection
  lastUpdater: "redux" | "ag";
  lastUpdatedHexId: HexId | null;
  lastUpdatedTaoProjectId: TaoProjectId | null;
};

const initialState: HexSelectionSliceState = {
  // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization -- no side effects
  selectedProjects: selectedProjectAdapter.getInitialState(),
  lastUpdater: "redux",
  lastUpdatedHexId: null,
  lastUpdatedTaoProjectId: null,
};

export const MAX_BULK_SELECTION_SIZE = 250;

const hexSelectionSlice = createSlice({
  name: "me",
  initialState,
  reducers: {
    select: (
      state,
      action: PayloadAction<{
        hex: HexSelectionData;
        lastUpdater?: "redux" | "ag";
      }>,
    ) => {
      if (
        state.selectedProjects.entities[`hex:${action.payload.hex.id}`] ==
          null &&
        state.selectedProjects.ids.length < MAX_BULK_SELECTION_SIZE
      ) {
        selectedProjectAdapter.addOne(state.selectedProjects, {
          selectionKey: `hex:${action.payload.hex.id}`,
          type: "hex",
          hex: action.payload.hex,
        });
        state.lastUpdatedHexId = action.payload.hex.id;
        state.lastUpdater = action.payload.lastUpdater ?? "redux";
      }
    },
    selectTao: (
      state,
      action: PayloadAction<{
        tao: TaoSelectionData;
        lastUpdater?: "redux" | "ag";
      }>,
    ) => {
      const { tao } = action.payload;
      if (
        state.selectedProjects.entities[`tao:${tao.taoProjectId}`] == null &&
        state.selectedProjects.ids.length < MAX_BULK_SELECTION_SIZE
      ) {
        selectedProjectAdapter.addOne(state.selectedProjects, {
          selectionKey: `tao:${tao.taoProjectId}`,
          type: "tao",
          tao,
        });
        state.lastUpdatedTaoProjectId = tao.taoProjectId;
        state.lastUpdater = action.payload.lastUpdater ?? "redux";
      }
    },
    updateData: (
      state,
      action: PayloadAction<{
        hexes: ReadonlyArray<
          Partial<HexSelectionData> & Pick<HexSelectionData, "id">
        >;
      }>,
    ) => {
      selectedProjectAdapter.updateMany(
        state.selectedProjects,
        action.payload.hexes.flatMap((hex) => {
          const selected = state.selectedProjects.entities[`hex:${hex.id}`];
          return selected?.type === "hex"
            ? [
                {
                  id: selected.selectionKey,
                  changes: {
                    hex: { ...selected.hex, ...hex },
                  },
                },
              ]
            : [];
        }),
      );
    },
    updateTaoData: (
      state,
      action: PayloadAction<{
        projects: ReadonlyArray<
          Partial<TaoSelectionData> & Pick<TaoSelectionData, "taoProjectId">
        >;
      }>,
    ) => {
      selectedProjectAdapter.updateMany(
        state.selectedProjects,
        action.payload.projects.flatMap((project) => {
          const selected =
            state.selectedProjects.entities[`tao:${project.taoProjectId}`];
          return selected?.type === "tao"
            ? [
                {
                  id: selected.selectionKey,
                  changes: {
                    tao: { ...selected.tao, ...project },
                  },
                },
              ]
            : [];
        }),
      );
    },
    deselect: (
      state,
      action: PayloadAction<{ hexId: HexId; lastUpdater?: "redux" | "ag" }>,
    ) => {
      const selectionKey = `hex:${action.payload.hexId}`;
      const prev = state.selectedProjects.entities[selectionKey];
      if (prev != null) {
        selectedProjectAdapter.removeOne(state.selectedProjects, selectionKey);
        state.lastUpdatedHexId = action.payload.hexId;
        state.lastUpdater = action.payload.lastUpdater ?? "redux";
      }
    },
    deselectTao: (
      state,
      action: PayloadAction<{
        taoProjectId: TaoProjectId;
        lastUpdater?: "redux" | "ag";
      }>,
    ) => {
      const selectionKey = `tao:${action.payload.taoProjectId}`;
      if (state.selectedProjects.entities[selectionKey] != null) {
        selectedProjectAdapter.removeOne(state.selectedProjects, selectionKey);
        state.lastUpdatedTaoProjectId = action.payload.taoProjectId;
        state.lastUpdater = action.payload.lastUpdater ?? "redux";
      }
    },
    deselectByPerm: (
      state,
      action: PayloadAction<{ perm: HexSelectionPerm }>,
    ) => {
      state.lastUpdatedHexId = null;
      for (const project of Object.values(state.selectedProjects.entities)) {
        if (
          project?.type === "hex" &&
          !hasHexSelectionPerm(project.hex, action.payload.perm)
        ) {
          selectedProjectAdapter.removeOne(
            state.selectedProjects,
            project.selectionKey,
          );
          state.lastUpdater = "redux";
        }
      }
    },
    deselectProjectsWithoutDeletePermission: (state) => {
      state.lastUpdatedHexId = null;
      state.lastUpdatedTaoProjectId = null;
      for (const project of Object.values(state.selectedProjects.entities)) {
        if (project != null && !projectHasDeletePermission(project)) {
          selectedProjectAdapter.removeOne(
            state.selectedProjects,
            project.selectionKey,
          );
          state.lastUpdater = "redux";
        }
      }
    },
    deselectProjectsWithoutMetadataPermission: (state) => {
      state.lastUpdatedHexId = null;
      state.lastUpdatedTaoProjectId = null;
      for (const project of Object.values(state.selectedProjects.entities)) {
        if (project != null && !projectHasMetadataPermission(project)) {
          selectedProjectAdapter.removeOne(
            state.selectedProjects,
            project.selectionKey,
          );
          state.lastUpdater = "redux";
        }
      }
    },
    clear: (state, _action: Action) => {
      state.lastUpdatedHexId = null;
      state.lastUpdatedTaoProjectId = null;
      if (state.selectedProjects.ids.length !== 0) {
        selectedProjectAdapter.removeAll(state.selectedProjects);
        state.lastUpdater = "redux";
      }
    },
  },
});

export const hexSelectionReducer = hexSelectionSlice.reducer;
export const hexSelectionActions = hexSelectionSlice.actions;

const selectState = (state: RootState): HexSelectionSliceState =>
  state.hexSelection;

// eslint-disable-next-line tree-shaking/no-side-effects-in-initialization -- no side effects
const selectedProjectSelectors = selectedProjectAdapter.getSelectors(
  (state: RootState) => selectState(state).selectedProjects,
);

const selectSelectedProjects = selectedProjectSelectors.selectAll;
const selectSelectedProjectKeys = createSelector(
  [selectSelectedProjects],
  (projects) => new Set(projects.map((project) => project.selectionKey)),
);
const selectSelectedHexes = createSelector(
  [selectSelectedProjects],
  (projects) =>
    projects.flatMap((project) =>
      project.type === "hex" ? [project.hex] : [],
    ),
);
const selectSelectedTaoProjects = createSelector(
  [selectSelectedProjects],
  (projects) =>
    projects.flatMap((project) =>
      project.type === "tao" ? [project.tao] : [],
    ),
);
const selectSelectedIds = createSelector([selectSelectedHexes], (hexes) =>
  hexes.map((hex) => hex.id),
) as (state: RootState) => HexId[];
const selectSelectedTaoIds = createSelector(
  [selectSelectedTaoProjects],
  (projects) => projects.map((project) => project.taoProjectId),
);
const selectSelectedCount = createSelector(
  [selectSelectedHexes],
  (hexes) => hexes.length,
);
const selectSelectedProjectCount = selectedProjectSelectors.selectTotal;
const selectAnySelected = (state: RootState): boolean =>
  selectSelectedProjectCount(state) > 0;

const selectSelectedIdsSet = createSelector(
  [selectSelectedIds],
  (hexIds) => new Set(hexIds),
);

const selectSelectedStatusIdsSet = createSelector(
  [selectSelectedProjects],
  (projects) =>
    new Set(
      projects.map((project) =>
        project.type === "hex"
          ? project.hex.status?.id
          : project.tao.status?.id,
      ),
    ),
);

const selectSelectedProjectLanguage = createSelector(
  [selectSelectedHexes],
  (hexes): ProjectLanguage | null => {
    const projectLanguage = hexes[0]?.projectLanguage ?? null;
    return hexes.every((hex) => hex.projectLanguage === projectLanguage)
      ? projectLanguage
      : null;
  },
);

const selectSelectedCountsByCategoryId = createSelector(
  [selectSelectedProjects],
  (projects) =>
    projects.reduce<Record<CategoryId, number>>((acc, project) => {
      const categories =
        project.type === "hex"
          ? project.hex.categories
          : project.tao.categories;
      for (const category of categories) {
        acc[category.id] = (acc[category.id] ?? 0) + 1;
      }
      return acc;
    }, {}),
);

const selectIsHexSelected = (state: RootState, hexId: HexId): boolean =>
  selectedProjectSelectors.selectById(state, `hex:${hexId}`)?.type === "hex";

const selectLastUpdater = (state: RootState): "redux" | "ag" =>
  selectState(state).lastUpdater;

const selectLastUpdatedHexId = (state: RootState): HexId | null =>
  selectState(state).lastUpdatedHexId;

const countPermSelector = (
  perm: HexSelectionPerm,
): ((state: RootState) => number) =>
  createSelector(
    [selectSelectedHexes],
    (hexes) => hexes.filter((hex) => hasHexSelectionPerm(hex, perm)).length,
  );

const countPerm: Record<HexSelectionPerm, (state: RootState) => number> = {
  canDelete: countPermSelector("canDelete"),
  canEdit: countPermSelector("canEdit"),
  canEditMetadata: countPermSelector("canEditMetadata"),
  canSetOwner: countPermSelector("canSetOwner"),
  canUseApp: countPermSelector("canUseApp"),
};

const createPermSelector = (
  perm: HexSelectionPerm,
): ((state: RootState) => boolean) =>
  createSelector([selectSelectedHexes], (hexes) =>
    hexes.every((hex) => hasHexSelectionPerm(hex, perm)),
  );

const selectPerm: Record<HexSelectionPerm, (state: RootState) => boolean> = {
  canDelete: createPermSelector("canDelete"),
  canEdit: createPermSelector("canEdit"),
  canEditMetadata: createPermSelector("canEditMetadata"),
  canSetOwner: createPermSelector("canSetOwner"),
  canUseApp: createPermSelector("canUseApp"),
};

const countSelectedProjectsWithDeletePermission = createSelector(
  [selectSelectedProjects],
  (projects) => projects.filter(projectHasDeletePermission).length,
);

const selectAllSelectedProjectsHaveDeletePermission = createSelector(
  [selectSelectedProjects],
  (projects) => projects.every(projectHasDeletePermission),
);

const countSelectedProjectsWithMetadataPermission = createSelector(
  [selectSelectedProjects],
  (projects) => projects.filter(projectHasMetadataPermission).length,
);

const selectAllSelectedProjectsHaveMetadataPermission = createSelector(
  [selectSelectedProjects],
  (projects) => projects.every(projectHasMetadataPermission),
);

const selectAnyTaoSelected = createSelector(
  [selectSelectedTaoProjects],
  (projects) => projects.length > 0,
);

function hasHexSelectionPerm(
  hex: HexSelectionData,
  perm: HexSelectionPerm,
): boolean {
  switch (perm) {
    case "canDelete":
      return hex.canDelete;
    case "canEdit":
      return hex.canEdit;
    case "canEditMetadata":
      return hex.canEditMetadata;
    case "canSetOwner":
      return hex.canSetOwner;
    case "canUseApp":
      return hex.canUseApp;
  }
}

export const hexSelectionSelectors = {
  selectSelectedProjects,
  selectSelectedProjectKeys,
  selectSelectedTaoProjects,
  selectSelectedTaoIds,
  selectSelectedProjectCount,
  countSelectedProjectsWithDeletePermission,
  selectAllSelectedProjectsHaveDeletePermission,
  countSelectedProjectsWithMetadataPermission,
  selectAllSelectedProjectsHaveMetadataPermission,
  selectAnyTaoSelected,
  selectSelectedIds,
  selectSelectedCount,
  selectAnySelected,
  selectSelectedIdsSet,
  selectSelectedStatusIdsSet,
  selectSelectedProjectLanguage,
  selectSelectedCountsByCategoryId,
  selectIsHexSelected,
  selectLastUpdater,
  selectLastUpdatedHexId,
  countPerm,
  selectPerm,
};

function projectHasDeletePermission(project: SelectedProject): boolean {
  return project.type === "hex" ? project.hex.canDelete : project.tao.canDelete;
}

function projectHasMetadataPermission(project: SelectedProject): boolean {
  return project.type === "hex"
    ? project.hex.canEditMetadata
    : project.tao.canEditMetadata;
}

export interface SelectionPerm {
  countWithPerm: number;
  hasPerm: boolean;
  deselectByPerm: () => void;
}

export function useSelectedProjectsDeletePermission(): SelectionPerm {
  const countWithPerm = useSelector(
    hexSelectionSelectors.countSelectedProjectsWithDeletePermission,
  );
  const hasPerm = useSelector(
    hexSelectionSelectors.selectAllSelectedProjectsHaveDeletePermission,
  );

  const dispatch = useDispatch();
  const deselectByPerm = useCallback(() => {
    dispatch(hexSelectionActions.deselectProjectsWithoutDeletePermission());
  }, [dispatch]);

  return {
    countWithPerm,
    hasPerm,
    deselectByPerm,
  };
}

export function useSelectedProjectsMetadataPermission(): SelectionPerm {
  const countWithPerm = useSelector(
    hexSelectionSelectors.countSelectedProjectsWithMetadataPermission,
  );
  const hasPerm = useSelector(
    hexSelectionSelectors.selectAllSelectedProjectsHaveMetadataPermission,
  );

  const dispatch = useDispatch();
  const deselectByPerm = useCallback(() => {
    dispatch(hexSelectionActions.deselectProjectsWithoutMetadataPermission());
  }, [dispatch]);

  return {
    countWithPerm,
    hasPerm,
    deselectByPerm,
  };
}

export function useHexSelectionPerm(perm: HexSelectionPerm): SelectionPerm {
  const countWithPerm = useSelector(hexSelectionSelectors.countPerm[perm]);
  const hasPerm = useSelector(hexSelectionSelectors.selectPerm[perm]);

  const dispatch = useDispatch();
  const deselectByPerm = useCallback(() => {
    dispatch(hexSelectionActions.deselectByPerm({ perm }));
  }, [dispatch, perm]);

  return {
    countWithPerm,
    hasPerm,
    deselectByPerm,
  };
}
